<script setup lang="ts">
import { t } from '@/i18n';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Menu, ChevronDown, LogOut, X, HelpCircle, PanelLeftClose, PanelLeftOpen } from '@lucide/vue';
import { useAuthStore } from '@/stores/auth';
import { useContextStore } from '@/stores/context';
import { useToast } from '@/composables/useToast';
import { useScrollLock } from '@/composables/useScrollLock';
import ModalDialog from '@/components/ModalDialog.vue';
import ThemeSelector from '@/components/ThemeSelector.vue';
import LanguageSelector from '@/components/LanguageSelector.vue';
import EnapresLogo from '@/components/EnapresLogo.vue';
import { applicationModules, getModuleForPath } from '@/lib/moduleRegistry';

const auth=useAuthStore(), context=useContextStore(), toast=useToast(), router=useRouter(), route=useRoute();
const sidebar=ref(false), profile=ref(false), help=ref(false), leaving=ref(false), collapsed=ref(false);
const menuButton=ref<HTMLButtonElement>(), sidePanel=ref<HTMLElement>();
const profileButton=ref<HTMLButtonElement>(), profileWrap=ref<HTMLElement>();
const mobileQuery=window.matchMedia('(max-width:760px)'), isMobile=ref(mobileQuery.matches);
const mobileMenuOpen=computed(()=>isMobile.value && sidebar.value);
const currentModule=computed(()=>getModuleForPath(route.path));
const dashboardActive=computed(()=>currentModule.value?.id==='dashboard');
const udraActive=computed(()=>currentModule.value?.id==='udra');
const rail=computed(()=>!isMobile.value && collapsed.value);
const dashboardExpanded=ref(dashboardActive.value);
useScrollLock(mobileMenuOpen);
try { collapsed.value=localStorage.getItem('udra.sidebarCollapsed')==='true'; } catch { /* Optional layout preference. */ }

function closeSidebar(restore=true){sidebar.value=false;if(restore)void nextTick(()=>menuButton.value?.focus());}
function closeProfile(restore=false){profile.value=false;if(restore)void nextTick(()=>profileButton.value?.focus());}
function updateViewport(){isMobile.value=mobileQuery.matches;closeSidebar(false);}
function setCollapsed(value:boolean){
  collapsed.value=value;
  try{localStorage.setItem('udra.sidebarCollapsed',String(value));}catch{/* Optional layout preference. */}
}
function toggleMenu(){
  closeProfile();
  if(isMobile.value){sidebar.value=!sidebar.value;}
  else setCollapsed(!collapsed.value);
}
function activateModule(id:string){
  if(id==='dashboard')dashboardExpanded.value=true;
  finishSidebarNavigation();
}
function finishSidebarNavigation(){
  closeSidebar(false);
  // Same-page links also close the mobile panel, so they must release focus from it.
  void nextTick(()=>document.getElementById('main-content')?.focus({preventScroll:true}));
}
function outsideProfile(event:PointerEvent){if(profile.value && !profileWrap.value?.contains(event.target as Node))closeProfile();}
function profileFocusOut(event:FocusEvent){if(event.relatedTarget && !profileWrap.value?.contains(event.relatedTarget as Node))closeProfile();}
function onKeydown(event:KeyboardEvent){
  if(event.key==='Escape'){
    if(mobileMenuOpen.value){event.preventDefault();closeSidebar();}
    else if(profile.value){event.preventDefault();closeProfile(true);}
  }
  if(event.key!=='Tab'||!mobileMenuOpen.value)return;
  const items=Array.from(sidePanel.value?.querySelectorAll<HTMLElement>('a[href],button:not(:disabled)') ?? []).filter(element=>!element.closest('[inert]') && element.getClientRects().length>0);
  if(!items?.length)return;
  const first=items[0]!,last=items[items.length-1]!;
  if(event.shiftKey && document.activeElement===first){event.preventDefault();last.focus();}
  else if(!event.shiftKey && document.activeElement===last){event.preventDefault();first.focus();}
}
watch(mobileMenuOpen,async open=>{if(open){await nextTick();sidePanel.value?.querySelector<HTMLButtonElement>('button')?.focus();}});
watch(help,async (open,previous)=>{if(previous&&!open){await nextTick();profileButton.value?.focus();}});
watch(profile,async open=>{if(open){await nextTick();profileWrap.value?.querySelector<HTMLButtonElement>('.profile-popover button')?.focus();}});
watch(()=>route.path,async()=>{
  if(dashboardActive.value)dashboardExpanded.value=true;
  closeSidebar(false);closeProfile();
  await nextTick();document.getElementById('main-content')?.focus({preventScroll:true});
});
watch(udraActive,active=>{if(active)void context.load().catch(error=>toast.error(error.message));},{immediate:true});
onMounted(()=>{
  mobileQuery.addEventListener('change',updateViewport);
  document.addEventListener('pointerdown',outsideProfile);
  document.addEventListener('keydown',onKeydown);
});
onBeforeUnmount(()=>{
  mobileQuery.removeEventListener('change',updateViewport);
  document.removeEventListener('pointerdown',outsideProfile);
  document.removeEventListener('keydown',onKeydown);
});
async function logout(){
  leaving.value=true;
  try{await auth.logout();await router.push('/login');}
  catch(error){toast.error((error as Error).message);}
  finally{leaving.value=false;}
}
</script>

<template>
  <a class="skip-link" href="#main-content" :inert="mobileMenuOpen">{{ t('shell.skip') }}</a>
  <header class="app-header" :inert="mobileMenuOpen">
    <RouterLink to="/udra/articulos" class="brand"><EnapresLogo/></RouterLink>
    <button ref="menuButton" class="header-menu" :aria-expanded="isMobile?sidebar:!collapsed" aria-controls="sidebar" :aria-label="t('shell.openMenu')" :title="isMobile?t('shell.menu'):collapsed?t('shell.expandMenu'):t('shell.collapseMenu')" @click="toggleMenu"><component :is="isMobile?Menu:collapsed?PanelLeftOpen:PanelLeftClose" aria-hidden="true"/></button>
    <div class="header-title"><strong>{{ t('shell.survey') }}</strong><span>{{ t('shell.title') }}</span></div>
    <div class="header-preferences"><LanguageSelector on-brand/><ThemeSelector on-brand/></div>
    <div ref="profileWrap" class="profile-wrap" @focusout="profileFocusOut">
      <button ref="profileButton" class="profile-button" :aria-expanded="profile" aria-controls="profile-options" :aria-label="t('shell.accountOptions',{name:auth.user?.name || t('shell.administration')})" @click="profile=!profile">
        <span class="avatar">UD</span><span><strong>{{auth.user?.name || t('shell.administration')}}</strong><small>{{auth.user?.role || t('shell.administrator')}}</small></span><ChevronDown aria-hidden="true"/>
      </button>
      <Transition name="popover">
        <div v-if="profile" id="profile-options" class="profile-popover" role="region" :aria-label="t('shell.account')">
          <strong>{{auth.user?.username}}</strong><small>{{auth.user?.role}}</small>
          <button @click="help=true;closeProfile()"><HelpCircle aria-hidden="true"/>{{ t('shell.about') }}</button>
          <button :disabled="leaving" @click="logout"><LogOut aria-hidden="true"/>{{leaving?t('shell.signingOut'):t('shell.signOut')}}</button>
        </div>
      </Transition>
    </div>
  </header>
  <button v-if="mobileMenuOpen" class="sidebar-scrim" tabindex="-1" :aria-label="t('shell.closeModuleMenu')" @click="closeSidebar()"/>
  <aside id="sidebar" ref="sidePanel" class="sidebar" :class="{open:sidebar,collapsed:rail}" :inert="isMobile && !sidebar" :role="mobileMenuOpen?'dialog':undefined" :aria-modal="mobileMenuOpen?true:undefined" :aria-label="t('shell.modules')">
    <div class="sidebar-heading"><div><span>{{ t('shell.workspace') }}</span><strong>{{ t('shell.modules') }}</strong></div><button class="icon-btn mobile-only" :aria-label="t('shell.closeMenu')" @click="closeSidebar()"><X aria-hidden="true"/></button></div>
    <nav :aria-label="t('shell.modules')">
      <div v-for="module in applicationModules" :key="module.id" class="module-group" :class="{'current-module':currentModule?.id===module.id}">
        <div class="module-heading">
          <RouterLink :to="module.path" class="module" :class="{active:currentModule?.id===module.id}" :aria-label="module.name" :title="rail?module.name:undefined" :aria-current="currentModule?.id===module.id?'true':undefined" @click="activateModule(module.id)"><span class="module-icon"><component :is="module.icon" aria-hidden="true"/></span><span class="module-label">{{module.name}}</span></RouterLink>
          <button v-if="module.id==='dashboard'" class="module-toggle" :aria-expanded="dashboardExpanded && !rail" aria-controls="dashboard-links" :aria-label="t(dashboardExpanded?'shell.collapseDashboard':'shell.expandDashboard')" @click="dashboardExpanded=!dashboardExpanded"><ChevronDown aria-hidden="true"/></button>
        </div>
        <div v-if="module.id==='dashboard'" id="dashboard-links" class="module-branch" :class="{expanded:dashboardExpanded && !rail}" :inert="!dashboardExpanded || rail" :aria-hidden="!dashboardExpanded || rail">
          <div class="module-branch-inner"><ul class="module-children"><li><RouterLink to="/dashboard/segmentacion" :class="{'section-active':dashboardActive}" :aria-current="dashboardActive?'page':undefined" @click="finishSidebarNavigation"><span>{{ t('shell.segmentation') }}</span><i v-if="dashboardActive" aria-hidden="true"/></RouterLink></li></ul></div>
        </div>
      </div>
    </nav>
    <div class="sidebar-footer"><strong>INEI</strong><p>{{ t('shell.institute') }}</p><span>{{udraActive?`ENAPRES / ${context.year}`:'ENAPRES'}}</span></div>
  </aside>
  <div class="workspace" :class="{expanded:!isMobile && collapsed}" :inert="mobileMenuOpen">
    <main id="main-content" tabindex="-1"><slot/></main>
    <footer class="workspace-footer"><span>{{currentModule?.footer || t('shell.title')}}</span><span>{{ t('shell.title') }}</span></footer>
  </div>
  <ModalDialog :open="help" :title="t('shell.title')" @close="help=false">
    <p>{{ t('shell.aboutDashboard') }}</p><p class="muted">{{ t('shell.aboutUdra') }}</p>
    <template #footer><button class="btn primary" @click="help=false">{{ t('shell.understood') }}</button></template>
  </ModalDialog>
</template>
